import api from './api.service';
import { TimeEntry, TimeBreak, TimeEntrySummary } from '../types/time-tracking.types';

// Proper types for API responses
interface TimeEntryResponse {
  success: boolean;
  data: TimeEntry | null;
}

interface TimeEntriesResponse {
  success: boolean;
  data: {
    entries: TimeEntry[];
    summary: TimeEntrySummary;
  };
}

interface TeamOverviewResponse {
  success: boolean;
  data: TimeEntry[];
}

interface BreakResponse {
  success: boolean;
  data: TimeBreak | null;
}

// Helper function for default TimeEntrySummary
function getDefaultTimeEntrySummary(): TimeEntrySummary {
  return {
    totalWorkTime: '00:00:00',
    totalBreakTime: '00:00:00',
    averageWorkTimePerDay: '00:00:00',
    daysWorked: 0
  };
}

class TimeTrackingService {

  async checkIn(location?: string, projectId?: string, notes?: string) {
    try {
      return await api.checkIn({ location, projectId, notes });
    } catch (error: unknown) {
      console.error('Check-in error:', error);
      throw error;
    }
  }

  async checkOut(notes?: string) {
    try {
      return await api.checkOut({ notes });
    } catch (error: unknown) {
      console.error('Check-out error:', error);
      throw error;
    }
  }

  async startBreak(_timeEntryId: string, breakType: 'lunch' | 'coffee' | 'other' = 'other') {
    try {
      return await api.startBreak({ breakType });
    } catch (error: unknown) {
      console.error('Start break error:', error);
      throw error;
    }
  }

  async endBreak(_breakId: string) {
    try {
      return await api.endBreak();
    } catch (error: unknown) {
      console.error('End break error:', error);
      throw error;
    }
  }

  async getTodayEntry(): Promise<TimeEntryResponse> {
    try {
      const response = await api.getTodayTimeEntry();
      return {
        success: response.success,
        data: response.data as TimeEntry | null
      };
    } catch (error: unknown) {
      console.error('Get today entry error:', error);
      throw error;
    }
  }

  async getActiveBreak(_timeEntryId: string): Promise<BreakResponse> {
    // Not implemented in backend yet, return mock
    return { success: true, data: null };
  }

  async getMyEntries(startDate: string, endDate: string): Promise<TimeEntriesResponse> {
    try {
      const response = await api.getMyTimeEntries({ startDate, endDate });
      const data = response.data as { entries: TimeEntry[]; summary: TimeEntrySummary } | undefined;
      return {
        success: response.success,
        data: data ?? { entries: [], summary: getDefaultTimeEntrySummary() }
      };
    } catch (error: unknown) {
      console.error('Get my entries error:', error);
      throw error;
    }
  }

  async getEmployeeEntries(userId: string, startDate: string, endDate: string): Promise<TimeEntriesResponse> {
    try {
      const response = await api.getEmployeeTimeEntries(userId, { startDate, endDate });
      const data = response.data as { entries: TimeEntry[]; summary: TimeEntrySummary } | undefined;
      return {
        success: response.success,
        data: data ?? { entries: [], summary: getDefaultTimeEntrySummary() }
      };
    } catch (error: unknown) {
      console.error('Get employee entries error:', error);
      throw error;
    }
  }

  async getTeamOverview(date: string): Promise<TeamOverviewResponse> {
    try {
      const response = await api.getTeamOverview(date);
      return {
        success: response.success,
        data: (response.data as TimeEntry[]) ?? []
      };
    } catch (error: unknown) {
      console.error('Get team overview error:', error);
      throw error;
    }
  }

  formatDuration(interval: string | null): string {
    if (!interval) return '0h 0m';
    const match = interval.match(/(\d+):(\d+):(\d+)/);
    if (match) {
      const hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      return `${hours}h ${minutes}m`;
    }
    return '0h 0m';
  }

  formatTime(timestamp: string | null): string {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  calculateElapsedTime(checkIn: string, totalBreakTime: string = '00:00:00'): string {
    const start = new Date(checkIn);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();

    // Subtract break time
    const breakMatch = totalBreakTime.match(/(\d+):(\d+):(\d+)/);
    let breakMs = 0;
    if (breakMatch) {
      breakMs = (parseInt(breakMatch[1]) * 3600 + parseInt(breakMatch[2]) * 60) * 1000;
    }

    const totalMs = Math.max(0, diffMs - breakMs);
    const hours = Math.floor(totalMs / 3600000);
    const minutes = Math.floor((totalMs % 3600000) / 60000);

    return `${hours}h ${minutes}m`;
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'checked_in':
        return '#10B981'; // Green
      case 'on_break':
        return '#F59E0B'; // Orange
      case 'checked_out':
        return '#3B82F6'; // Blue
      case 'not_checked_in':
        return '#9CA3AF'; // Light Gray
      default:
        return '#6B7280'; // Gray
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'checked_in':
        return 'Arbeitet';
      case 'on_break':
        return 'Pause';
      case 'checked_out':
        return 'Feierabend';
      case 'not_checked_in':
        return 'Nicht eingecheckt';
      default:
        return 'Offline';
    }
  }
}

const timeTrackingService = new TimeTrackingService();
export default timeTrackingService;
